import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import Swiper from 'https://cdn.jsdelivr.net/npm/swiper@12/swiper-bundle.min.mjs';

class CartDrawerRecommendationsSlider extends Component {
  /**
   * Abort controller for the active recommendations fetch (if there is one).
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    this.initializeAttempts = 0;
    this.initialize();
    this.loadRecommendations();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.cancelAnimationFrame(this.initializeFrame);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    this.#activeFetch?.abort();
    this.#activeFetch = null;
    if (this.swiper) this.swiper.destroy(true, true);
    this.swiper = undefined;
  }

  updatedCallback() {
    super.updatedCallback();
    this.syncSlider();
  }

  onCartUpdate = (event) => {
    event.promise
      ?.then(() => {
        this.loadRecommendations();
        this.syncSlider();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer-recommendations] Event promise rejected:', error);
      });
  };

  /**
   * Re-renders the slides against the live cart.
   *
   * Every source is rendered server side so products already in the cart are filtered out, and
   * products removed from the cart come back. With items in the cart the top-up list from the theme
   * settings supplies the products, or the Product Recommendations API when there is no list (the
   * snippet leaves `recommendationsUrl` empty when there is one); an empty cart falls back to the
   * theme settings collection.
   */
  async loadRecommendations() {
    const { recommendationsUrl, recommendationsSectionUrl } = this.dataset;
    if (!recommendationsSectionUrl) return;

    const wrapper = this.querySelector('[data-cart-drawer-recommendations-slides]');
    if (!wrapper) return;

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();
    const { signal } = this.#activeFetch;

    try {
      const seedProductId = recommendationsUrl ? await this.#getSeedProductId(signal) : undefined;

      // Recommendations need a product to recommend against; without one render the collection.
      const url = seedProductId ? `${recommendationsUrl}&product_id=${seedProductId}` : recommendationsSectionUrl;

      const response = await fetch(url, { signal });
      if (!response.ok) throw new Error(`Server returned ${response.status}`);

      const slides = new DOMParser()
        .parseFromString(await response.text(), 'text/html')
        .querySelector('[data-cart-drawer-recommendations-slides]');

      if (!slides || !this.isConnected) return;

      wrapper.replaceChildren(...slides.children);
      this.syncSlider();
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.warn('[cart-drawer-recommendations] Failed to load recommendations:', error);
      }
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Reads the most recently added product from the live cart, so the seed is never stale after an
   * add or a remove.
   * @param {AbortSignal} signal
   * @returns {Promise<number | undefined>}
   */
  async #getSeedProductId(signal) {
    const response = await fetch(`${Shopify.routes.root}cart.js`, { signal });
    if (!response.ok) throw new Error(`Server returned ${response.status}`);

    const cart = await response.json();
    return cart.items?.at(-1)?.product_id;
  }

  syncSlider() {
    if (!this.isConnected) return;
    if (!this.swiper) {
      this.initialize();
      return;
    }
    this.swiper.update();
  }

  initialize() {
    if (this.swiper || !this.isConnected) return;
    this.swiper = new Swiper(this, {
      slidesPerView: 2,
      spaceBetween: 0,
      speed: 450,
      threshold: 8,
      grabCursor: true,
      simulateTouch: true,
      watchOverflow: true,
      watchSlidesProgress: true,
      observer: true,
      observeParents: true,
      resistanceRatio: 0.65,
      mousewheel: {
        enabled: true,
        forceToAxis: true,
        sensitivity: 1,
        releaseOnEdges: true,
      },
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },
      a11y: {
        enabled: true,
        containerRoleDescriptionMessage: 'Product carousel',
        itemRoleDescriptionMessage: 'Product slide',
      },
      breakpoints: {
        750: {
          slidesPerView: 2,
          spaceBetween: 8,
        },
      },
    });
  }
}

if (!customElements.get('cart-drawer-recommendations-slider')) {
  customElements.define('cart-drawer-recommendations-slider', CartDrawerRecommendationsSlider);
}
